import { cn } from '@apps/web/lib/utils';
import {
    ArtifactEvidence,
    ArtifactFrame,
    ArtifactLifecycleStatusBadge,
} from '@apps/web/routes/_app/admin/agent/artifacts/frame';
import {
    type AdminArtifactRendererProps,
    BoundedArtifactPayload,
    isRecord,
    stripUntrustedMarkers,
    toRecord,
    truncateArtifactText,
} from '@apps/web/routes/_app/admin/agent/artifacts/shared';

const MAX_DIFF_RENDER_LINES = 400;
const MAX_JSON_CHANGE_ROWS = 100;
const MAX_JSON_CHANGE_VALUE_CHARS = 200;

type JsonChange = {
    readonly path: string;
    readonly changeType: string;
    readonly before: unknown;
    readonly after: unknown;
};

function parseJsonChanges(payload: Record<string, unknown>): Array<JsonChange> {
    const rawChanges = payload['json_changes'];
    if (!Array.isArray(rawChanges)) {
        return [];
    }
    return rawChanges.filter(isRecord).map((change) => ({
        path: typeof change['path'] === 'string' ? change['path'] : '$',
        changeType:
            typeof change['change_type'] === 'string'
                ? change['change_type']
                : 'changed',
        before: change['before'],
        after: change['after'],
    }));
}

function formatChangeValue(value: unknown): string {
    let serialized: string;
    try {
        serialized = JSON.stringify(value) ?? 'null';
    } catch {
        serialized = String(value);
    }
    if (serialized.length > MAX_JSON_CHANGE_VALUE_CHARS) {
        return `${serialized.slice(0, MAX_JSON_CHANGE_VALUE_CHARS - 1)}…`;
    }
    return serialized;
}

function changeToneClassName(changeType: string): string {
    switch (changeType) {
        case 'added':
            return 'bg-green-50 text-green-700';
        case 'removed':
            return 'bg-red-50 text-red-700';
        default:
            return 'bg-muted text-muted-foreground';
    }
}

function JsonChangesView({ changes }: { readonly changes: Array<JsonChange> }) {
    const visibleChanges = changes.slice(0, MAX_JSON_CHANGE_ROWS);
    return (
        <div className="space-y-1.5">
            {visibleChanges.map((change, index) => (
                <div
                    key={`${change.path}-${index}`}
                    className="border-border/60 rounded-md border px-2.5 py-1.5 text-[12px]"
                >
                    <div className="flex min-w-0 items-center gap-2">
                        <span
                            className={cn(
                                'shrink-0 rounded-sm px-1.5 py-0.5 font-mono text-[10px] leading-none',
                                changeToneClassName(change.changeType),
                            )}
                        >
                            {change.changeType}
                        </span>
                        <span className="text-foreground min-w-0 truncate font-mono">
                            {change.path}
                        </span>
                    </div>
                    {change.changeType !== 'added' ? (
                        <p className="mt-1 font-mono text-[11px] break-all text-red-700">
                            - {formatChangeValue(change.before)}
                        </p>
                    ) : null}
                    {change.changeType !== 'removed' ? (
                        <p className="mt-0.5 font-mono text-[11px] break-all text-green-700">
                            + {formatChangeValue(change.after)}
                        </p>
                    ) : null}
                </div>
            ))}
            {changes.length > visibleChanges.length ? (
                <p className="text-muted-foreground text-[11px]">
                    Showing {visibleChanges.length.toLocaleString()} of{' '}
                    {changes.length.toLocaleString()} changes — the rest are in
                    the raw payload below.
                </p>
            ) : null}
        </div>
    );
}

function diffLineClassName(line: string): string {
    if (line.startsWith('+') && !line.startsWith('+++')) {
        return 'bg-green-50 text-green-800';
    }
    if (line.startsWith('-') && !line.startsWith('---')) {
        return 'bg-red-50 text-red-800';
    }
    if (line.startsWith('@@') || line.startsWith('diff ')) {
        return 'text-muted-foreground';
    }
    return 'text-foreground';
}

function UnifiedDiffView({ diffText }: { readonly diffText: string }) {
    const { text: boundedText, omittedChars } = truncateArtifactText(diffText);
    const allLines = boundedText.split('\n');
    const visibleLines = allLines.slice(0, MAX_DIFF_RENDER_LINES);
    const truncated = omittedChars > 0 || allLines.length > visibleLines.length;
    return (
        <div>
            <pre className="border-border/60 rounded-md border font-mono text-[12px] leading-5">
                {visibleLines.map((line, index) => (
                    <div
                        key={index}
                        className={cn(
                            'px-3 whitespace-pre-wrap',
                            diffLineClassName(line),
                        )}
                    >
                        {line || ' '}
                    </div>
                ))}
            </pre>
            {truncated ? (
                <p className="text-muted-foreground mt-1 text-[11px]">
                    Diff truncated for display — the full diff is in the raw
                    payload below.
                </p>
            ) : null}
        </div>
    );
}

/**
 * Renderer for diff artifacts (sandbox exported diffs, run-data diffs):
 * unified text diffs render as colored lines, structural JSON diffs as
 * per-path change rows. Rendering is size-capped; the full payload
 * stays behind the raw toggle.
 */
export function DiffOutputArtifact({ artifact }: AdminArtifactRendererProps) {
    const payload = toRecord(artifact.payload);
    const rawDiff = payload['diff'] ?? payload['unified_diff'];
    const diffText =
        typeof rawDiff === 'string' ? stripUntrustedMarkers(rawDiff) : '';
    const jsonChanges = parseJsonChanges(payload);
    return (
        <ArtifactFrame
            artifact={artifact}
            status={
                <ArtifactLifecycleStatusBadge
                    lifecycleStatus={artifact.lifecycle_status}
                />
            }
        >
            {diffText.trim().length > 0 ? (
                <UnifiedDiffView diffText={diffText} />
            ) : jsonChanges.length > 0 ? (
                <JsonChangesView changes={jsonChanges} />
            ) : (
                <p className="text-muted-foreground text-xs">Empty diff.</p>
            )}
            <ArtifactEvidence artifact={artifact} />
            <details className="group">
                <summary className="text-muted-foreground hover:text-foreground cursor-pointer text-xs transition-colors">
                    Raw JSON payload
                </summary>
                <div className="border-border/60 mt-1.5 border-l pl-3">
                    <BoundedArtifactPayload payload={artifact.payload} />
                </div>
            </details>
        </ArtifactFrame>
    );
}
